import { UnknownObject } from '@monofront/types';

export const sortObjectKeys = <TObject extends Record<string, unknown>>(
  obj: TObject,
) =>
  Object.keys(obj)
    .sort()
    .reduce(
      (acc, key) => ({
        ...acc,
        [key]: obj[key],
      }),
      {},
    );

const isObject = (object: unknown): object is object => {
  return object != null && typeof object === 'object';
};

export const areObjectsEqual = (a: unknown, b: unknown): boolean => {
  const visited = new WeakSet();

  const compare = (objA: unknown, objB: unknown): boolean => {
    if (objA === objB) {
      return true;
    }

    if (!isObject(objA) || !isObject(objB)) {
      return false;
    }

    // Check for circular reference
    if (visited.has(objA)) {
      return objA === objB;
    }

    visited.add(objA);

    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);

    if (keysA.length !== keysB.length) {
      return false;
    }

    for (const key of keysA) {
      if (!keysB.includes(key)) {
        return false;
      }

      if (
        !compare(
          (objA as Record<string, unknown>)[key],
          (objB as Record<string, unknown>)[key],
        )
      ) {
        return false;
      }
    }

    return true;
  };

  return compare(a, b);
};

/**
 * Remove specified properties from an object.
 * @param obj The object to omit properties from.
 * @param keys The keys of the properties to omit.
 * @returns A new object with the specified properties omitted.
 */
export const omit = <TObject extends UnknownObject, TKey extends keyof TObject>(
  obj: TObject,
  ...keys: Array<TKey>
): Omit<TObject, TKey> => {
  const result = { ...obj };

  keys.forEach((key) => {
    delete result[key];
  });

  return result;
};
